import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { Navigation } from 'app/core/navigation/navigation.types';
import { DtsService } from 'app/modules/admin/campaign/details/tabs/dts/dts.service';
import { catchError, map, Observable, of, ReplaySubject, switchMap, tap } from 'rxjs';

/** Menu entry of the Drive to Store page, shown only to organizations with DTS enabled. */
const DTS_NAVIGATION_ID = 'drive-to-store';

@Injectable({providedIn: 'root'})
export class NavigationService {
    private _httpClient = inject(HttpClient);
    private _dtsService = inject(DtsService);
    private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data
     *
     * The Drive to Store entry depends on a backend allowlist, not on the role that `forRole` checks, so
     * it is removed here when `GET /dts/visit-lift/enabled` says no — fail-closed, like the DTS tab and
     * the route guard: an error hides it too.
     */
    get(): Observable<Navigation> {
        return this._httpClient.get<Navigation>('api/common/navigation').pipe(
            switchMap((navigation) =>
                this._dtsService.getEnabled().pipe(
                    map(({ enabled }) => enabled === true),
                    catchError(() => of(false)),
                    map((enabled) => (enabled ? navigation : withoutItem(navigation, DTS_NAVIGATION_ID))),
                ),
            ),
            tap((navigation) => {
                this._navigation.next(navigation);
            }),
        );
    }
}

function withoutItem(navigation: Navigation, id: string): Navigation {
    const prune = (items: FuseNavigationItem[] | undefined): FuseNavigationItem[] | undefined =>
        items
            ?.filter((item) => item.id !== id)
            .map((item) => (item.children ? { ...item, children: prune(item.children) } : item));

    return {
        compact: prune(navigation.compact) ?? [],
        default: prune(navigation.default) ?? [],
        futuristic: prune(navigation.futuristic) ?? [],
        horizontal: prune(navigation.horizontal) ?? [],
    };
}
